// src/portal/PortalSubmitterMap.jsx
//
// The submitter's map. VECTOR-ONLY ON A NEUTRAL BASEMAP — decision 14.
//
// ── NO TOKEN, AND THAT IS THE POINT ─────────────────────────────────────────
//
// basemapTileUrl (stores/useBasemapStore.js) returns a MAPBOX url with
// getConfig().mapboxToken in the query string, for all six of its styles. This
// uses the tokenless ESRI raster pattern PlatformCoverageAoiMap already carries,
// which needs no token, no /tile/session, and no entitlement.
//
// A submitter is a third party. Handing them a tile session means resolving an
// entitlement for an org whose imagery they have no seat for, and the portal has
// no reason to: they are checking that the geometry they uploaded is where they
// think it is, against roads and boundaries. Aerial imagery is the REVIEWER's
// tool, because the reviewer is the one judging a setback against what is
// actually on the ground.
//
// ── NO MlDraw ───────────────────────────────────────────────────────────────
//
// A submitter uploads geometry; they do not draw it. ml-draw.js's source and
// layer ids are constant strings ("ml-draw-temp", and nine layer literals in
// both addLayer and destroy), so two instances on ONE map collide on
// addSource -- and even overriding the three id fields would leave destroy()
// removing the first instance's layers by literal. Two instances on two
// different maps are fine, which is the shape this portal uses.
import { useEffect, useRef } from "react";
import { useFeatureSelection } from "./useFeatureSelection";
import { syncMarkupLabels } from "./portalMarkupLabels";
import maplibregl from "maplibre-gl";
import { whenMapLoaded } from "../lib/map/whenMapLoaded";
import { attachAttribution } from "../lib/map/attribution";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  ESRI_NEUTRAL_STYLE, fitToFeatures, addSubmissionLayers, addMarkupLayers,
} from "./portalMapStyle";

/**
 * @param {object}   p
 * @param {Array}    p.features
 * @param {number|null} [p.selectedOrdinal]  the feature the panel considers selected
 * @param {Array} [p.markup]  the findings on this revision, as route 6 shaped
 *   them. See "THE SUBMITTER SEES WHAT WAS SENT" below.
 * @param {Function|null} [p.onSelectFeature] called with an ordinal, or null for a
 *   click on empty map. ABSENT means a read-only map — the submitted-revision
 *   view on PortalReviewPage passes neither prop and behaves exactly as before.
 *
 * ── THE SUBMITTER SEES WHAT WAS SENT ────────────────────────────────────────
 *
 * Markup rendering was added to PortalReviewerMap only (§3.25), so a reviewer
 * drew three findings, returned the plan, and the submitter got three list rows
 * and an empty map. The row was right, route 6 was right, and this map had no
 * markup layer. The fifth instance this phase of a thing built on the surface
 * it was being tested against and absent on the one it was not.
 *
 * ── THE SAME FUNCTION, NOT A SECOND STYLE ───────────────────────────────────
 *
 * addMarkupLayers is already shared (portalMapStyle.js:245) and this calls it.
 * Nothing is extracted and nothing is copied: §3.22's collision and the M10
 * mutation are both about a SECOND definition of one meaning drifting from the
 * first, and a markup style that lived in two files would be that again.
 *
 * ── NO CLIENT-SIDE FROZEN FILTER ────────────────────────────────────────────
 *
 * Route 6 already withholds unfrozen markup from a submitter
 * (planReviewView.js, maySeeUnfrozenMarkup). Re-filtering here would be a
 * second copy of the server's disclosure rule, in the place least able to
 * enforce it. Everything that arrives is frozen, so addMarkupLayers' dash
 * expression draws it all solid — DERIVED from what arrived, not asserted.
 *
 * ── THE PINK OVER A NEUTRAL BASEMAP, MEASURED ───────────────────────────────
 *
 * `imagery` is null for a submitter by design — a dataset key names a product
 * they have no seat for — so markup renders over the basemap, and #f472b6 had
 * only ever been judged against imagery. On #0C0E10 it is 7.30:1, above WCAG's
 * 3.0 non-text threshold and close to `other`'s 7.67:1, and it sits ≥58 dE from
 * every submitter colour. No restyle. A number is not an eye, so the
 * walkthrough is still where that gets settled.
 */
export default function PortalSubmitterMap({
  features, markup = [], selectedOrdinal = null, onSelectFeature = null,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  // THE SHARED WIRING. Both portal maps call this; PortalReviewerMap gained it
  // with the same commit. The stale-closure guard and the clear-the-previous
  // highlight live there, not here.
  const { registerClickHandlers } = useFeatureSelection({
    mapRef, features, selectedOrdinal, onSelectFeature,
  });

  useEffect(() => {
    if (!containerRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: ESRI_NEUTRAL_STYLE,
      center: [-102, 32],
      zoom: 7,
      attributionControl: { compact: false },
    });
    // Esri's credit and "Powered by Esri" (REM-684).
    attachAttribution(map);
    map.getCanvas().style.background = "var(--map-backdrop-rgb, #0C0E10)";
    mapRef.current = map;

    whenMapLoaded(map, () => {
      addSubmissionLayers(map, features);
      // AFTER the submission, so a finding draws on top of the shape it is
      // about — the same ordering the reviewer map uses, for the same reason.
      addMarkupLayers(map, markup);
      syncMarkupLabels(map, markup);
      fitToFeatures(map, features);

      registerClickHandlers(map);
    });

    // PlatformCoverageAoiMap's teardown, minus the MlDraw half it does not have.
    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Features arriving after load — a second revision, or a slow fetch.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    addSubmissionLayers(map, features);
    addMarkupLayers(map, markup);
    syncMarkupLabels(map, markup);
    fitToFeatures(map, features);
  }, [features, markup]);



  return <div ref={containerRef} className="h-full w-full" />;
}
